import {useMemo, useState} from 'react'
import {useQuery} from '@apollo/client'
import {Alert, Badge, Button, Card, Spinner, Table} from 'react-bootstrap'
import {parseId} from '../../../helpers'
import {
  GET_ACA_VERIFICATIONS_BY_POLICY,
  IResACAVerifications,
  ACAVerificationRecord,
} from '../../../gql/queries/acaVerificationsQuery'
import {ModalACAVerification} from './ModalACAVerification'

type ResultColor = 'green' | 'yellow' | 'red' | 'blue'

const RESULT_CONFIG: Record<ResultColor, {label: string; bg: string; textClass: string}> = {
  green:  {label: 'Paid & Completed',       bg: '#198754', textClass: 'text-white'},
  yellow: {label: 'Paid / Pending Docs',    bg: '#ffc107', textClass: 'text-dark'},
  red:    {label: 'Unpaid / Pending',       bg: '#dc3545', textClass: 'text-white'},
  blue:   {label: '$0 Premium / Completed', bg: '#0d6efd', textClass: 'text-white'},
}

const ResultBadge = ({result}: {result: string}) => {
  const cfg = RESULT_CONFIG[result as ResultColor]
  if (!cfg) return <span className='text-muted'>—</span>
  return (
    <span
      className={`badge ${cfg.textClass}`}
      style={{backgroundColor: cfg.bg}}
    >
      {cfg.label}
    </span>
  )
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

const userLabel = (u?: {firstName?: string; lastName?: string} | null) => {
  if (!u) return '—'
  return `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'
}

type Props = {
  policyId: string
  documentsDueDate?: string | null
  hasOpenTickets: boolean
  canAdd: boolean
  canChange: boolean
  members?: import('../../../types/policiesApi').EdgeMemberPolicy[]
}

export const ACAVerificationsTab = ({
  policyId,
  documentsDueDate,
  hasOpenTickets,
  canAdd,
  canChange,
  members = [],
}: Props) => {
  const normalizedId = useMemo(() => Number(parseId(policyId)), [policyId])

  const {data, loading, error, refetch} = useQuery<IResACAVerifications>(
    GET_ACA_VERIFICATIONS_BY_POLICY,
    {
      variables: {policyId: normalizedId},
      skip: !normalizedId,
      fetchPolicy: 'no-cache',
    }
  )

  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ACAVerificationRecord | undefined>()

  const openNew = () => {
    setEditTarget(undefined)
    setModalOpen(true)
  }

  const openEdit = (v: ACAVerificationRecord) => {
    setEditTarget(v)
    setModalOpen(true)
  }

  const handleClose = () => {
    setModalOpen(false)
    setEditTarget(undefined)
  }

  const handleSaved = () => {
    refetch()
  }

  const records = data?.acaVerificationsByPolicy ?? []

  return (
    <div className='cardContainer'>
      <Card>
        <Card.Header className='d-flex align-items-center justify-content-between'>
          <span className='fw-bold'>
            <i className='bi bi-clipboard2-check me-2 text-primary' />
            ACA Verifications
          </span>
          {canAdd && (
            <Button size='sm' variant='primary' onClick={openNew}>
              <i className='bi bi-plus-lg me-1' />
              New Verification
            </Button>
          )}
        </Card.Header>

        <Card.Body className='p-0'>
          {loading && (
            <div className='text-center py-4'>
              <Spinner animation='border' size='sm' />
              <span className='ms-2 text-muted'>Loading verifications…</span>
            </div>
          )}

          {error && (
            <Alert variant='danger' className='m-3'>
              Error loading verifications: {error.message}
            </Alert>
          )}

          {!loading && !error && records.length === 0 && (
            <div className='text-center text-muted py-4 fst-italic'>
              No ACA verifications recorded yet.
            </div>
          )}

          {!loading && records.length > 0 && (
            <Table hover responsive className='mb-0' size='sm'>
              <thead className='table-light'>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Result</th>
                  <th>Payment</th>
                  <th>Docs</th>
                  <th>Last updated by</th>
                  {canChange && <th />}
                </tr>
              </thead>
              <tbody>
                {records.map((v) => (
                  <tr key={v.id}>
                    <td className='text-nowrap'>{formatDate(v.created)}</td>
                    <td>{userLabel(v.createdBy)}</td>
                    <td>
                      <ResultBadge result={v.result} />
                    </td>
                    <td>
                      {v.paymentStatus === 'up_to_date' && (
                        <Badge bg='success'>Up to date</Badge>
                      )}
                      {v.paymentStatus === 'pending' && (
                        <Badge bg='warning' text='dark'>Pending</Badge>
                      )}
                      {v.paymentStatus === 'zero_payment' && (
                        <Badge bg='secondary'>$0 Payment</Badge>
                      )}
                      {!v.paymentStatus && '—'}
                    </td>
                    <td>
                      {v.pendingDocuments === 'yes' && (
                        <Badge bg='danger'>Pending</Badge>
                      )}
                      {v.pendingDocuments === 'no' && (
                        <Badge bg='success'>None</Badge>
                      )}
                      {v.pendingDocuments === 'na' && (
                        <Badge bg='secondary'>N/A</Badge>
                      )}
                      {!v.pendingDocuments && '—'}
                    </td>
                    <td className='text-muted small'>
                      {v.updatedBy && v.updated
                        ? `${userLabel(v.updatedBy)} · ${formatDate(v.updated)}`
                        : '—'}
                    </td>
                    {canChange && (
                      <td>
                        <Button
                          size='sm'
                          variant='outline-secondary'
                          onClick={() => openEdit(v)}
                        >
                          <i className='bi bi-pencil' />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {modalOpen && (
        <ModalACAVerification
          isOpen={modalOpen}
          onClose={handleClose}
          policyId={policyId}
          documentsDueDate={documentsDueDate}
          hasOpenTickets={hasOpenTickets}
          verificationToEdit={editTarget}
          onSaved={handleSaved}
          canChange={canChange}
          members={members}
        />
      )}
    </div>
  )
}
